
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useInputValidation } from "@/hooks/use-input-validation";

interface AdapterMountDialogProps {
  onAddMount: (mount: string) => void;
  children: React.ReactNode;
}

export function AdapterMountDialog({ onAddMount, children }: AdapterMountDialogProps) {
  const [open, setOpen] = useState(false);
  const { value: mountType, handleChange, error, handleBlur } = useInputValidation({
    maxLength: 4,
    pattern: /^[a-zA-Z0-9]*$/,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (mountType.trim() && !error) {
      onAddMount(mountType.trim().toUpperCase());
      handleChange("");
      setOpen(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow alphanumeric input
    const value = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
    handleChange(value.slice(0, 4)); // Limit to 4 characters
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Add Adapter Mount</DialogTitle>
            <DialogDescription>
              Enter the mount type for your adapter (max 4 alphanumeric characters)
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="space-y-2">
              <Label htmlFor="mount">Mount Type</Label>
              <Input
                id="mount"
                value={mountType}
                onChange={handleInputChange}
                onBlur={handleBlur}
                placeholder="e.g. M42, EF, F"
                maxLength={4}
                className="uppercase"
              />
              {error && (
                <p className="text-sm text-destructive">{error.message}</p>
              )}
              <p className="text-xs text-muted-foreground">
                Examples: M42, EF, F, E (max 4 characters)
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!!error || !mountType.trim()}>Add Mount</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
